'use client';

import { useEffect, useRef, useState } from 'react';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon, ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { usePodcastPlayer } from '../../Context/Podcast/PodcastPlayerContext';
import { useAudioMediaControls } from '../../Hooks/useAudioMediaControls';
import { PlayerControls } from './PlayerControls';
import { PlaybackRatePopover } from './PlaybackRatePopover';
import { TimeSlider } from './TimeSlider';

const formatTime = (seconds) => `${Math.floor(seconds / 60)}:${(seconds % 60).toFixed(0).toString().padStart(2, '0')}`;

export const PodcastPlayer = () => {
  const [isMounted, setIsMounted] = useState(false);
  const {
    isVisible,
    isAudio,
    streamUrl,
    playing,
    setPlaying,
    time,
    setTime,
    duration,
    setDuration,
    image,
    mini,
    setMini,
    playbackRate,
    setPlaybackRate,
    episodeNumber,
    episodeTitle,
    accentColor,
    slug,
    resetPlayer
  } = usePodcastPlayer();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const rates = [
    { value: 0.75, label: '0.75x' },
    { value: 1.0, label: '1.0x' },
    { value: 1.25, label: '1.25x' },
    { value: 1.5, label: '1.5x' },
    { value: 1.75, label: '1.75x' },
    { value: 2.0, label: '2.0x' }
  ];

  const audioRef = useRef();
  const initialTimeRestored = useRef(false);
  const savedTimeRef = useRef(time);

  // Keep savedTimeRef in sync with time prop
  useEffect(() => {
    savedTimeRef.current = time;
  }, [time]);

  useAudioMediaControls(audioRef, { setPlaying, time, setTime, setDuration, initialTimeRestored });

  // Restore saved time position when audio element becomes available or streamUrl changes
  useEffect(() => {
    if (!streamUrl) return;

    // Wait for audio element to be available
    const checkAudioAndRestore = () => {
      const audio = audioRef.current;

      if (!audio) {
        // Retry after a short delay if audio element isn't ready yet
        setTimeout(checkAudioAndRestore, 10);
        return;
      }

      // Reset the flag when streamUrl changes (new episode)
      initialTimeRestored.current = false;

      const restoreTime = () => {
        const savedTime = Number(savedTimeRef.current);

        if (savedTime > 0) {
          audio.currentTime = savedTime;
        }
        // Mark as restored so timeupdate can start updating
        initialTimeRestored.current = true;
      };

      const handleLoadedMetadata = () => {
        restoreTime();
      };

      const handleCanPlay = () => {
        if (!initialTimeRestored.current) {
          restoreTime();
        }
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('canplay', handleCanPlay);

      // Check if metadata is already loaded (readyState >= HAVE_METADATA = 1)
      if (audio.readyState >= 1) {
        restoreTime();
      }

      // Cleanup function
      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('canplay', handleCanPlay);
      };
    };

    const cleanup = checkAudioAndRestore();

    return () => {
      if (cleanup) cleanup();
    };
  }, [streamUrl]);

  // Single source of truth for play/pause control
  // This effect responds to changes in the `playing` state
  // Also depends on isMounted to ensure audio element exists
  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || !isMounted) return;

    if (playing && audio.paused) {
      // User wants to play and audio is currently paused
      // Wait for initial time restoration before playing
      const attemptPlay = () => {
        if (!initialTimeRestored.current) {
          // Wait a bit for time restoration to complete
          setTimeout(attemptPlay, 50);
          return;
        }

        audio.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            setPlaying(false);
          } else {
            console.error('Error playing audio:', error);
          }
        });
      };

      attemptPlay();
    } else if (!playing && !audio.paused) {
      // User wants to pause and audio is currently playing
      audio.pause();
    }
  }, [playing, setPlaying, isMounted]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  // Prevent hydration mismatch by only rendering after mount
  if (!isMounted || !isVisible) return null;

  return (
    <div className="fixed right-5 bottom-5 z-100" role="region" aria-label="Podcast Player">
      {isAudio && (
        <div
          className={`grid ${mini ? 'h-px-60 w-px-68' : 'h-px-44 w-px-200'} ${mini ? 'grid-cols-4 gap-y-4' : 'grid-cols-[1fr_1fr_1fr_1fr_8fr_1fr_1fr_1fr] gap-x-1'} ${mini ? 'grid-rows-[1fr_4fr_1fr_1fr]' : 'grid-rows-[1fr_4fr_3fr]'} place-items-center rounded-[10px] bg-white p-4 shadow-md outline-1 outline-gray-200 transition-all duration-500 ease-in-out`}
          aria-live="polite"
        >
          <img
            src={image}
            alt={`Podcast episode ${episodeNumber} cover image - ${episodeTitle}`}
            className={`col-span-2 col-start-1 row-span-2 row-start-1 self-start justify-self-start rounded-lg ${mini ? 'h-px-30 w-px-30' : 'h-px-24 w-px-24'}`}
          />
          {!mini && (
            <button
              aria-label="Share"
              className="col-start-6 row-start-1 cursor-pointer self-center border-0 bg-transparent p-0 opacity-100 transition-all duration-500 ease-in-out focus-visible:outline-2"
              onClick={() => {
                navigator.clipboard.writeText(`${process.env.NEXT_PUBLIC_DOMAIN}/resources/podcast/${slug}`);
                toast.success('Copied link to clipboard');
              }}
            >
              <ArrowUpOnSquareIcon className="size-px-5" aria-hidden="true" />
            </button>
          )}
          <button
            aria-label={mini ? 'Expand player' : 'Minimize player'}
            className={`${mini ? 'col-start-4 row-start-1 justify-self-start opacity-100' : 'col-start-7 row-start-1 opacity-0'} focus-visible:outline-pdTeal size-px-5 hidden cursor-pointer self-center border-0 bg-transparent p-0 transition-all duration-500 ease-in-out focus-visible:outline-2 lg:block`}
            onClick={() => setMini(!mini)}
            tabIndex={mini ? 0 : -1}
          >
            <ArrowsPointingOutIcon className="size-px-5" aria-hidden="true" />
          </button>
          <button
            aria-label={mini ? 'Expand player' : 'Minimize player'}
            className={`${mini ? 'col-start-4 row-start-1 justify-self-start opacity-0' : 'col-start-7 row-start-1 opacity-100'} focus-visible:outline-pdTeal size-px-5 hidden cursor-pointer self-center border-0 bg-transparent p-0 transition-all duration-500 ease-in-out focus-visible:outline-2 lg:block`}
            onClick={() => setMini(!mini)}
            tabIndex={mini ? -1 : 0}
          >
            <ArrowsPointingInIcon className="size-px-5" aria-hidden="true" />
          </button>
          <button
            aria-label="Close player"
            className={`${mini ? 'col-start-4 row-start-1 justify-self-end' : 'col-start-8 row-start-1'} focus-visible:outline-pdTeal size-px-6 cursor-pointer border-0 bg-transparent p-0 focus-visible:outline-2`}
            onClick={resetPlayer}
          >
            <XCircleIcon className="size-px-6" aria-hidden="true" />
          </button>
          <header
            className={`${mini ? 'col-span-2 row-start-2 text-[60px] leading-[78.67px] tracking-[4.42px]' : 'text-px-lg col-span-4 col-start-3 row-start-1 self-start justify-self-start leading-loose tracking-widest'} flex items-center justify-start gap-2 font-bold uppercase`}
            style={{ color: accentColor }}
          >
            {!mini && <span className="align-self-start">Episode</span>}
            {episodeNumber.toString().padStart(3, '0')}
          </header>
          {!mini && (
            <h2 className="align-self-start text-px-xl text-px-gray-900 col-span-4 col-start-3 row-start-2 justify-self-start font-black whitespace-pre-line transition-all">
              {episodeTitle}
            </h2>
          )}
          {/* Controls */}
          <PlayerControls audioRef={audioRef} playing={playing} setPlaying={setPlaying} mini={mini} />
          {/* Playback Rate */}
          <PlaybackRatePopover
            rates={rates}
            playbackRate={playbackRate}
            setPlaybackRate={setPlaybackRate}
            mini={mini}
          />
          {/* Time Display */}
          <span
            className={`${mini ? 'col-start-1 row-start-4' : 'col-start-4 row-start-3'} w-px-10`}
            aria-label={`Current time: ${formatTime(time)}`}
          >
            {formatTime(time)}
          </span>
          {/* Time Slider */}
          <TimeSlider
            time={time}
            duration={duration}
            accentColor={accentColor}
            audioRef={audioRef}
            setTime={setTime}
            mini={mini}
          />
          <span
            className={`${mini ? 'col-start-4 row-start-4' : 'col-start-7 row-start-3'}`}
            aria-label={`Total duration: ${formatTime(duration)}`}
          >
            {formatTime(duration)}
          </span>
          <audio ref={audioRef} src={streamUrl} aria-hidden="true" controlsList="nodownload" />
        </div>
      )}
    </div>
  );
};
